/* eslint-disable sonarjs/cognitive-complexity */
import React, { MutableRefObject, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash-es';
import { getCheckoutCreatorStore, ModifyCheckoutMetaData, SaveStart, SetIsTemplateOpen, SetCheckoutErrors, StartEditStep, TemplateDeselect, ModifyEmailTemplate, SetUrlPayload } from '@/store/checkoutCreator/actions';
import { CheckoutCreatorStatus, CheckoutErrorNames} from '@/store/checkoutCreator/types';
import { useAccessLevel } from '@/services/hooks/useAccessLevel';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { ShowDialogParams } from '@/store/dialog/types';
import { ShowDialog } from '@/store/dialog/actions';
import { getAccountAdvancedOptions } from '@/store/account/actions';
import { StepTitle } from '../components/StepTitle';
import { StepContent } from '../components/StepContent';
import { Step1Content } from '../steps/Step1Content';
import { Step1EditContent } from '../steps/Step1EditContent';
import { Step2Content } from '../steps/Step2Content';
import { Step2EditContent } from '../steps/Step2EditContent';
import { getStepsValidity } from '../tools/getStepsValidity';
import { getStepsStatuses } from '../tools/getStepsStatuses';
import { Step3Content } from '../steps/Step3Content';
import { Step3EditContent } from '../steps/Step3EditContent';
import { Step4Content } from '../steps/Step4Content';
import { Step4EditContent } from '../steps/Step4EditContent';
import { Step5Content } from '../steps/Step5Content';
import { useLandingPageSubdomain } from './useLandingPageSubdomain';
import { Step5EditContent } from '../steps/Step5EditContent';
import { isCheckoutCreated } from '../tools/isCheckoutCreated';
import { useGetDefaultSender } from '../../../services/hooks/useGetDefaultSender';
import { FooterContent } from '../steps/FooterContent';

interface StepsProps {
    onExit: () => void;
    isSavedRef: MutableRefObject<boolean>;
    isGalleryCreator: boolean;
}

export const useSteps = ({ onExit, isSavedRef, isGalleryCreator }: StepsProps): any[] => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {changeSubdomain, accountSubdomain,checkoutBaseUrl} = useLandingPageSubdomain()
    const advancedOptions = useSelector(getAccountAdvancedOptions);
    const {email: defaultEmail, name: defaultName } = useGetDefaultSender() ?? {};
    const { stepInEdit, checkoutObject, checkoutPayload, template, status, errors, checkoutMetaDataObject, checkoutMetaDataPayload, urlPayload, postPurchaseTemplatePayload, isEmailTemplateOpen } = useSelector(getCheckoutCreatorStore);
    const isCreated = isCheckoutCreated(checkoutObject);
    const isSaving = status === CheckoutCreatorStatus.SAVING;
    const isStep3Visible = !advancedOptions?.IsOwnedByReseller;
    const stepsValidity = getStepsValidity(isStep3Visible, template, checkoutObject, checkoutMetaDataObject);
    const stepsStatuses = getStepsStatuses(stepsValidity, stepInEdit);
    const { hasAccessLevel } = useAccessLevel();
    const canModifyCheckouts = hasAccessLevel(AccessLevel.ModifyLandingPagesCheckout);
    const step5Ref = useRef<HTMLDivElement>();
    const isEmailDesignerOpenRef = useRef<boolean>();
    useEffect(() => {
        if (isEmailTemplateOpen) { isEmailDesignerOpenRef.current = true; return; }
        if (isEmailDesignerOpenRef.current) { isEmailDesignerOpenRef.current = false; step5Ref.current?.scrollIntoView?.(); }
    },[isEmailTemplateOpen]);
    useEffect((): void => {
        isSavedRef.current = !(checkoutPayload || checkoutMetaDataPayload || urlPayload || postPurchaseTemplatePayload || !stepsValidity['5']);
    }, [checkoutPayload,checkoutObject,checkoutMetaDataPayload,urlPayload, postPurchaseTemplatePayload])
    const editTemplate = (): void => {
        dispatch(SetIsTemplateOpen(true));
    };
    const deselectTemplate = (): void => {
        dispatch(StartEditStep(2));
        dispatch(TemplateDeselect()); // this order is important, first step edit then deselect
    };
    const replaceTemplate = (): void => {
        const dialogParams: ShowDialogParams = {
            title: t('replacelandingpage'),
            confirmLabel: t('confirm'),
            body: t('replacelandingpadedescription'),
            onClickConfirm: deselectTemplate,
            cancelLabel: t('cancel'),
        };
        dispatch(ShowDialog(dialogParams));
    };
    const setCustomLandingPageDomain = () => {
        const { ProductLanding } = checkoutObject;
        const landingUrl = urlPayload ?? checkoutBaseUrl;
        //prevent from going further if there is an error 
        if(errors?.landing_invalid_suffix){
            return;
        }
        if(!accountSubdomain) {
            dispatch(SetCheckoutErrors({
                [CheckoutErrorNames.LACK_OF_ACCOUNT_SUBDOMAIN]: true
            }))
            return;
        }
        return changeSubdomain({LandingPageID: ProductLanding?.LandingPageID, Url: landingUrl}).then(()=>{
                dispatch(SaveStart({}));                
            }).catch(({errorKey})=>{
                const errors = {
                ...(errorKey === CheckoutErrorNames.SUBDOMAIN_LANDING_SUBDIRECTORY_IN_USE && {[CheckoutErrorNames.SUBDOMAIN_LANDING_SUBDIRECTORY_IN_USE]: true}),
                ...(errorKey === CheckoutErrorNames.INVALID_SUFFIX && {[CheckoutErrorNames.INVALID_SUFFIX]: true})
            }
        
            dispatch(SetCheckoutErrors(errors))
        })
    };
    const setEmptyLandingMetaJson = (): void => {
        if (!checkoutObject?.ProductLanding?.LandingMetaJson && !checkoutMetaDataObject?.data?.socialPreview && !checkoutMetaDataPayload) {
            dispatch(ModifyCheckoutMetaData({ data: { socialPreview: { pageTitle: '', pageDescription: '', imageUrl: '' }} }));
        }
        dispatch(SaveStart({}));
    };
    const getIsStep1SaveDisabled = () => {
        if (!isCreated && (!checkoutPayload?.Name || !checkoutPayload?.ProductPrice || !checkoutPayload?.DownloadUrl)) {
            return true;
        }
        if  (!checkoutPayload) {
            return true;
        }
        return checkoutPayload?.Name === '' || checkoutPayload?.DownloadUrl === '';
    };
    const setDefaultStep5Data = (): void => {
        if (isNil(postPurchaseTemplatePayload?.fromEmail) && !checkoutObject?.PostPurchaseTemplate?.FromEmail) {
            dispatch(ModifyEmailTemplate({ fromEmail: defaultEmail }));
        }
        if (isNil(postPurchaseTemplatePayload?.fromName) && isNil(checkoutObject?.PostPurchaseTemplate?.FromName)) {
            dispatch(ModifyEmailTemplate({ fromName: defaultName }));
        }
        if (isNil(postPurchaseTemplatePayload?.subject) && isNil(checkoutObject?.PostPurchaseTemplate?.Body?.Subject)) {
            dispatch(ModifyEmailTemplate({ subject: t('checkoutpostpurchasedefaultsubject') }));
        }
        const errorsToCheck = [CheckoutErrorNames.EMPTY_CHECKOUT_AUDIENCE];
        dispatch(SaveStart({errorsToCheck}));
    };
    const isStep5SaveDisabled = 
        postPurchaseTemplatePayload?.fromEmail===''
        || postPurchaseTemplatePayload?.subject === ''
        || (checkoutPayload?.CheckoutAudience && !checkoutPayload?.CheckoutAudience.length) 
        || !checkoutObject?.PostPurchaseTemplate?.Body?.Parts;

    const isEditAvailable = canModifyCheckouts;
    const isEditDisabled = !isEditAvailable || isSaving;
    return [
        {
            title: <StepTitle stepNumber={1} isEditVisible={stepsStatuses['1'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('product')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={getIsStep1SaveDisabled()}
                    isEditMode={isEditAvailable && stepsStatuses['1'] === 'active'}
                    isNew={!stepsValidity['1']}
                    content={<Step1Content />}
                    editContent={<Step1EditContent />}

                />
            ),
            status: stepsStatuses['1'],
            hasErrors: stepInEdit === 1 || !stepsValidity['1'],
        },
        {
            title: <StepTitle 
                stepNumber={2} 
                isEditVisible={stepsStatuses['2'] === 'done-open'} 
                isEditDisabled={isEditDisabled} 
                title={t('design')}
                customButtonTitle1={t('replace')}
                customButtonTitle2={t('edittemplate')}
                customButtonAction1={replaceTemplate}
                customButtonAction2={editTemplate}
                skipEditStepWarning1
            />,
            content: (
                <StepContent
                    isSaveVisible={false}
                    isSaveDisabled
                    isEditMode={isEditAvailable && stepsStatuses['2'] === 'active'}
                    isNew={!stepsValidity['2']}
                    content={<Step2Content />}
                    editContent={<Step2EditContent /> }
                />
            ),
            status: stepsStatuses['2'],
            hasErrors: stepInEdit === 2 || !stepsValidity['2'],
        },
        ...(isStep3Visible ? [{
            title: <StepTitle stepNumber={3} isEditVisible={stepsStatuses['3'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('checkoutlink')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={errors?.landing_subdirectory_in_use || errors?.landing_invalid_suffix}  
                    isEditMode={isEditAvailable && stepsStatuses['3'] === 'active'}
                    isNew={!stepsValidity['3']}
                    content={<Step3Content />} 
                    editContent={<Step3EditContent />}
                    beforeSave
                    onSave={setCustomLandingPageDomain}
                />
            ),
            status: stepsStatuses['3'],
            hasErrors: stepInEdit === 3 || !stepsValidity['3'],
        }] : []),
        {
            title: <StepTitle stepNumber={4} isEditVisible={stepsStatuses['4'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('lp_creator_social_header')} />,
            content: (
                <StepContent 
                    isSaveVisible
                    isSaveDisabled={false}  
                    isEditMode={isEditAvailable && stepsStatuses['4'] === 'active'}
                    isNew={!stepsValidity['4']}
                    content={<Step4Content />} 
                    editContent={<Step4EditContent />}
                    onSave={setEmptyLandingMetaJson}
                />
            ),
            status: stepsStatuses['4'],
            hasErrors: stepInEdit === 4 || !stepsValidity['4'],
        },
        {
            title: <StepTitle stepNumber={5} isEditVisible={stepsStatuses['5'] === 'done-open'} isEditDisabled={isEditDisabled} title={t('postpurchasesettings')} />,
            content: (
                <StepContent 
                    wrapperRef={step5Ref}
                    isSaveVisible
                    isSaveDisabled={isStep5SaveDisabled}
                    isEditMode={isGalleryCreator || (isEditAvailable && stepsStatuses['5'] === 'active')}
                    isNew={!stepsValidity['5']}
                    content={<Step5Content isEditAvailable={isEditAvailable} />}
                    editContent={<Step5EditContent />}
                    onSave={setDefaultStep5Data}
                />
            ),
            status: isGalleryCreator ? 'active' : stepsStatuses['5'],
            hasErrors: stepInEdit === 5 || !stepsValidity['5'],
        },
        {
            title: '',
            content: <FooterContent onExit={onExit} isStep5Valid={stepsValidity['5']} isEditAvailable={isEditAvailable} />,
            status: 'content-only',
            hasErrors: false,
        },
    ];
};
